import React, { useCallback } from 'react';
import { useAsyncValue, useRevalidator } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DocumentNode } from '@apollo/client';

// Components
import { RoutePresetsManagementTable } from '../../../../components/display/tables/formTables/managementTables/RoutePresetsManagementTable';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';

// Types
import { RoutePresetsManagementLoaderData } from './types';
import { RoutePresetRow } from '../../../../components/display/tables/formTables/managementTables/RoutePresetsManagementTable/types';

// Redux Actions
import { openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../../components/modals/SuperModal/types';

// Styles
import { AddButtonRow, Container, RoutePresetsSection } from './styles';

// GQL
import { client } from '../../../../gql/client';
import { evictSurveillanceRoutePresets } from '../../../../gql/cacheHelpers';
import {
  DELETE_SURVEILLANCE_ROUTE_PRESET,
  SET_SURVEILLANCE_ROUTE_PRESET_ACTIVE,
} from '../../../../gql/mutations/surveillanceRoutePresets';

// Utils
import { showBackendErrorToast } from '../../../../utils/toasts';

/**
 * Catálogo de plantillas de recorrido (Gestión).
 *
 * Estructura calcada de Empresas externas: dos tablas activas | inactivas y las
 * acciones por `SuperModal`. La diferencia es que el alta también va por modal
 * —una plantilla no es un nombre suelto: son carreteras y un rango de PK por
 * cada una— en vez de por un formulario permanente sobre la tabla.
 */
export default function RoutePresetsManagementPage(): JSX.Element {
  const loaderData = useAsyncValue() as RoutePresetsManagementLoaderData;
  const revalidator = useRevalidator();
  const dispatch = useDispatch();
  const { t } = useTranslation(['pages']);

  const onReFetchData = useCallback(() => {
    revalidator.revalidate();
  }, [revalidator]);

  /**
   * Acciones de fila. El error va por toast siguiendo la doctrina del
   * proyecto: `showBackendErrorToast` traduce el código estable del backend
   * (`SR_PRESET_NOT_FOUND`, `SR_PK_OUTSIDE_ROAD`…) y cae a un genérico si es un
   * error técnico.
   */
  const runMutation = useCallback(
    async (mutation: DocumentNode, variables: Record<string, unknown>) => {
      try {
        await client.mutate({
          mutation,
          variables,
          refetchQueries: ['GetSurveillanceRoutePresets'],
          awaitRefetchQueries: true,
        });
        // 🔴 El `refetchQueries` de arriba solo refresca la variante que esta
        // pantalla consulta (`onlyActive: false`). El asistente lee la OTRA
        // (`onlyActive: true`), que para Apollo es una entrada distinta y se
        // quedaría con la lista vieja. Sin esto, una plantilla recién creada no
        // aparece al salir a ruta.
        evictSurveillanceRoutePresets(client.cache);
        onReFetchData();
      } catch (error) {
        showBackendErrorToast(
          error,
          t('pages:management.errors.routePresetGeneric')
        );
      }
    },
    [onReFetchData, t]
  );

  const openPresetModal = useCallback(
    (presetId?: string) => {
      dispatch(
        openModal(modalIds.EDIT_ROUTE_PRESET_MODAL, {
          preset: presetId ? loaderData.presetsById[presetId] : undefined,
          roads: loaderData.roads,
          onSuccess: onReFetchData,
        })
      );
    },
    [dispatch, loaderData.presetsById, loaderData.roads, onReFetchData]
  );

  const onDeletePreset = useCallback(
    (preset: RoutePresetRow) => {
      dispatch(
        openModal(modalIds.DELETE_ROUTE_PRESET_MODAL, {
          presetName: preset.name,
          onConfirmDelete: async () => {
            await runMutation(DELETE_SURVEILLANCE_ROUTE_PRESET, {
              id: preset.id,
            });
          },
        })
      );
    },
    [dispatch, runMutation]
  );

  return (
    <Container>
      <RoutePresetsSection>
        <AddButtonRow>
          <MainButton
            type="button"
            text={t('pages:management.addRoutePreset')}
            onClick={() => openPresetModal()}
          />
        </AddButtonRow>

        <RoutePresetsManagementTable
          data={{
            activePresets: loaderData.activePresets,
            inactivePresets: loaderData.inactivePresets,
          }}
          onDeactivate={(id) =>
            runMutation(SET_SURVEILLANCE_ROUTE_PRESET_ACTIVE, {
              id,
              isActive: false,
            })
          }
          onActivate={(id) =>
            runMutation(SET_SURVEILLANCE_ROUTE_PRESET_ACTIVE, {
              id,
              isActive: true,
            })
          }
          onEditPreset={(preset) => openPresetModal(preset.id)}
          onDeletePreset={onDeletePreset}
        />
      </RoutePresetsSection>
    </Container>
  );
}
